import React from 'react';
import moment from 'moment-timezone';
import { ArrowRightOutlined } from '@ant-design/icons';
import { StarBadge, StarBadgeKind } from './StarBadge';
import { formatReminderTime, parseRemindAt, STAR_ACTION } from '../../data/workOrderStarLabels';

interface StarLog {
	starAction?: string;
	isStarred?: boolean;
	starNote?: string | null;
	remindAt?: string | number | null;
	previousRemindAt?: string | number | null;
	changedAt?: string;
}

// The notes-log line for a track/reminder audit row, e.g. "Tracked and set Reminder for 4:30 PM
// "Discuss with manager"" or "Snoozed Reminder: 3:45 PM → 6:45 PM". Times read relative to the
// day the change was made.
const StarLogPrefix: React.FC<{ log: StarLog }> = ({ log }) => {
	const loggedAt = log.changedAt ? moment(log.changedAt) : moment();
	const remindAt = parseRemindAt(log.remindAt);
	const previousRemindAt = parseRemindAt(log.previousRemindAt);
	const time = (value: moment.Moment | null) => (value ? formatReminderTime(value, loggedAt) : '');
	const action =
		log.starAction || (log.isStarred === false ? STAR_ACTION.UNSTARRED : STAR_ACTION.STARRED);

	let badge: StarBadgeKind = 'bell';
	let message: React.ReactNode;
	let showNote = false;
	switch (action) {
		case STAR_ACTION.STARRED:
			badge = remindAt ? 'bell' : 'star';
			message = remindAt ? `Tracked and set Reminder for ${time(remindAt)}` : 'Tracked';
			showNote = true;
			break;
		case STAR_ACTION.UNSTARRED:
			badge = 'untracked';
			message = previousRemindAt || remindAt ? 'Untracked and Removed Reminder' : 'Untracked';
			showNote = true;
			break;
		case STAR_ACTION.NOTE_UPDATED:
			badge = remindAt ? 'bell' : 'star';
			message = 'Updated tracking note';
			showNote = true;
			break;
		case STAR_ACTION.REMINDER_SET:
			message = `Set Reminder for ${time(remindAt)}`;
			break;
		case STAR_ACTION.REMINDER_UPDATED:
		case STAR_ACTION.REMINDER_SNOOZED:
			message = (
				<>
					{action === STAR_ACTION.REMINDER_SNOOZED ? 'Snoozed Reminder:' : 'Changed Reminder:'}{' '}
					<span className="font-normal text-character-secondary">
						{time(previousRemindAt)} <ArrowRightOutlined className="mx-1" /> {time(remindAt)}
					</span>
				</>
			);
			break;
		case STAR_ACTION.REMINDER_STOPPED:
			badge = 'star';
			message = `Stopped Reminder for ${time(previousRemindAt)}`;
			break;
		case STAR_ACTION.REMINDER_REMOVED:
			badge = 'star';
			message = `Removed Reminder for ${time(previousRemindAt)}`;
			break;
		default:
			message = 'Tracking changed';
	}

	const isUntracked = action === STAR_ACTION.UNSTARRED;
	// "Tracked: "note"" — but "Tracked and set Reminder for 4:30 PM "note"" (design).
	const noteColon = showNote && !!log.starNote && !isUntracked && !remindAt;
	return (
		<span className="inline-flex items-center">
			<StarBadge badge={badge} size="sm" />
			<span className="font-medium">
				{message}
				{noteColon ? ':' : ''}
			</span>
			{showNote && log.starNote ? (
				<span
					className={`ml-1 italic text-character-secondary ${isUntracked ? 'line-through' : ''}`}
				>{`"${log.starNote}"`}</span>
			) : null}
		</span>
	);
};

export default StarLogPrefix;
